import { useState, useEffect } from "react";
import { BiHome, BiLogOut, BiKey } from "react-icons/bi";
import { useNavigate } from "react-router";

import { Stack, Text, Box, Table } from "@chakra-ui/react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { getAstreinteMailsAircallMfaAstreinteMailsGetOptions } from "@/codegen/openapi2/@tanstack/react-query.gen";
import { CenteredLayout } from "@/components/2/CenteredLayout";
import { AppConfig } from "@/components/app-config";

// Helper function to format relative time in French
const formatRelativeTime = (dateString: string): string => {
  const receivedAt = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - receivedAt.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "moins d'une minute";
  if (diffMinutes === 1) return "1 minute";
  return `${diffMinutes} minutes`;
};

// Helper to check if code is older than 8 minutes
const isExpired = (dateString: string): boolean => {
  const receivedAt = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - receivedAt.getTime();
  const diffMinutes = diffMs / 60000;
  return diffMinutes > 8;
};

// Helper to extract astreinte number from email address
const extractAstreinteNumber = (email: string): number => {
  const match = email.match(/bonjour\+astreinte(\d+)@/);
  return match ? parseInt(match[1], 10) : 0;
};

export const MfaAircall = () => {
  const navigate = useNavigate();
  const { data: astreinteMails, isFetching } = useSuspenseQuery({
    ...getAstreinteMailsAircallMfaAstreinteMailsGetOptions(),
    refetchInterval: 15000,
  });

  // Countdown for next refresh
  const [countdown, setCountdown] = useState(15);

  // Reset countdown when fetch completes
  useEffect(() => {
    if (!isFetching) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resetting countdown on fetch completion is intentional
      setCountdown(15);
    }
  }, [isFetching]);

  // Decrement countdown every second (only when not fetching)
  useEffect(() => {
    if (isFetching) return;

    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? prev : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isFetching]);

  // Sort astreinte entries by number
  const sortedAstreinteEntries = Object.entries(astreinteMails).sort(
    ([emailA], [emailB]) => extractAstreinteNumber(emailA) - extractAstreinteNumber(emailB)
  );

  return (
    <CenteredLayout maxW="lg">
      <Stack gap="6">
        <Stack gap={{ base: "2", md: "3" }} textAlign="center">
          <Text color="fg.muted" fontWeight="semibold">
            Codes MFA Aircall Astreinte
          </Text>
          <Text color="fg.muted">
            Consulte les codes MFA reçus pour les lignes d'astreinte Aircall.
          </Text>
        </Stack>
      </Stack>

      <Stack
        gap="4"
        borderRadius="md"
        textStyle="sm"
        borderColor="blackAlpha.300"
        borderWidth={1}
        p={6}
      >
        <Stack direction="row" align="center" gap={2}>
          <Box as={BiKey} />
          <Text fontWeight="semibold" fontSize="md">
            MFA Aircall
          </Text>
        </Stack>

        {sortedAstreinteEntries.length === 0 ? (
          <Text color="fg.muted" fontSize="sm">
            Aucun code MFA disponible pour le moment.
          </Text>
        ) : (
          <Table.Root size="sm" rounded="md" borderWidth={1} overflow="hidden">
            <Table.Header>
              <Table.Row backgroundColor="gray.100">
                <Table.ColumnHeader>Astreinte</Table.ColumnHeader>
                <Table.ColumnHeader>Code</Table.ColumnHeader>
                <Table.ColumnHeader>Recu il y a</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedAstreinteEntries.map(([emailAddr, info]) => {
                const astreinteNum = extractAstreinteNumber(emailAddr);
                const expired = isExpired(info.received_at);
                return (
                  <Table.Row key={emailAddr} backgroundColor={expired ? "orange.100" : undefined}>
                    <Table.Cell>Astreinte {astreinteNum}</Table.Cell>
                    <Table.Cell fontFamily="mono" fontWeight="semibold">
                      {info.code}
                    </Table.Cell>
                    <Table.Cell>{formatRelativeTime(info.received_at)}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        )}

        <Text color="fg.muted" fontSize="xs" textAlign="center">
          {isFetching
            ? "Vérification en cours..."
            : `Prochaine vérification dans ${countdown} seconde${countdown > 1 ? "s" : ""}.`}
        </Text>
      </Stack>

      <Stack direction="row" align="center" justify="center" gap={6} mt={4}>
        <Box
          as="button"
          onClick={() => navigate("/")}
          display="flex"
          alignItems="center"
          gap={2}
          color="fg.muted"
          fontSize="sm"
          cursor="pointer"
          _hover={{ textDecoration: "underline" }}
        >
          <BiHome />
          <Text>Retour à l'accueil</Text>
        </Box>
        <Box
          as="button"
          onClick={AppConfig.logout}
          display="flex"
          alignItems="center"
          gap={2}
          color="fg.muted"
          fontSize="sm"
          cursor="pointer"
          _hover={{ textDecoration: "underline" }}
        >
          <BiLogOut />
          <Text>Se déconnecter</Text>
        </Box>
      </Stack>
    </CenteredLayout>
  );
};
